import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type AuthRole = 'admin' | 'funcionario' | 'master';

interface AuthContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  email: string | null;
  role: AuthRole | null;
  userId: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface SessionPayload {
  authenticated: boolean;
  email: string;
  role?: AuthRole;
  userId?: string | null;
}

async function requestSession(): Promise<SessionPayload> {
  const response = await fetch('/api/auth/session');
  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new Error(payload?.error || 'Sessão inválida.');
  }

  return payload as SessionPayload;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [role, setRole] = useState<AuthRole | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const refreshSession = async () => {
    try {
      const payload = await requestSession();
      setIsAuthenticated(Boolean(payload.authenticated));
      setEmail(payload.email ?? null);
      setRole(payload.role ?? null);
      setUserId(payload.userId ?? null);
    } catch {
      setIsAuthenticated(false);
      setEmail(null);
      setRole(null);
      setUserId(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refreshSession();
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    isLoading,
    isAuthenticated,
    email,
    role,
    userId,
    login: async (nextEmail: string, password: string) => {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: nextEmail,
          password,
        }),
      });

      const text = await response.text();
      const payload = text ? JSON.parse(text) : null;
      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao autenticar.');
      }

      setIsAuthenticated(true);
      setEmail(payload?.email ?? nextEmail);
      setRole((payload?.role ?? null) as AuthRole | null);
      // userId vem na próxima checagem de sessão; chama refresh pra padronizar.
      await refreshSession();
    },
    logout: async () => {
      await fetch('/api/auth/logout', {
        method: 'POST',
      });
      setIsAuthenticated(false);
      setEmail(null);
      setRole(null);
      setUserId(null);
    },
    refreshSession,
  }), [email, isAuthenticated, isLoading, role, userId]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
